import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Mail, MessageSquare, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";

export default function Contact() {
  const [email, setEmail] = useState("hello@arsenalartists.co");
  const [discord, setDiscord] = useState("");
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => {
      if (r.data?.contact_email) setEmail(r.data.contact_email);
      if (r.data?.discord_url) setDiscord(r.data.discord_url);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 sm:pt-28 pb-24">
      <Seo title="Contact" description="Reach Arsenal Artists — project enquiries, general questions and business enquiries." path="/contact" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Eyebrow>CONTACT</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Talk to <span className="text-arsenal">the studio.</span></h1>

        <div className="mt-14 grid lg:grid-cols-[1fr_1.1fr] gap-10">
          <div className="space-y-4">
            <Reveal>
              <Link to="/start-a-project" data-testid="contact-project-card" className="group block border border-arsenal/40 bg-arsenal/10 p-7 hover:bg-arsenal/15 transition-colors">
                <Briefcase className="h-6 w-6 text-arsenal" />
                <h2 className="mt-4 font-display text-xl font-bold text-white">Project enquiry</h2>
                <p className="mt-2 text-sm text-slate-400">Have actual work in mind? The project request form gets you a proper quote, fast.</p>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-arsenal">START A PROJECT <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </Link>
            </Reveal>
            <Reveal delay={0.08}>
              <a href={`mailto:${email}`} data-testid="contact-email-card" className="group block border border-white/10 bg-ink-surface p-7 hover:border-white/25 transition-colors">
                <Mail className="h-6 w-6 text-arsenal" />
                <h2 className="mt-4 font-display text-xl font-bold text-white">Email</h2>
                <p className="mt-2 text-sm text-slate-400">Business enquiries, partnerships and anything formal.</p>
                <span className="mt-4 inline-block font-mono text-sm text-steel group-hover:text-arsenal transition-colors">{email}</span>
              </a>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="border border-white/10 bg-ink-surface p-7" data-testid="contact-discord-card">
                <MessageSquare className="h-6 w-6 text-arsenal" />
                <h2 className="mt-4 font-display text-xl font-bold text-white">Discord community</h2>
                <p className="mt-2 text-sm text-slate-400">Hang out with the studio and the wider Arsenal community.</p>
                {discord && (
                  <a
                    href={discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-discord-btn"
                    className="clip-btn mt-5 inline-flex items-center gap-2 border border-white/20 hover:border-arsenal hover:text-arsenal text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors"
                  >
                    JOIN THE COMMUNITY <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-ink-surface p-8 sm:p-10" data-testid="contact-form-card">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-arsenal" />
                  <h2 className="mt-5 font-display text-2xl font-bold">Message received.</h2>
                  <p className="mt-2 text-slate-400">We'll get back to you — usually within a couple of business days.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <h2 className="font-display text-xl font-bold">General enquiry</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Name *</span>
                      <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="aa-input" />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Email *</span>
                      <input required type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="aa-input" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Topic</span>
                    <select data-testid="contact-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="aa-input">
                      <option value="general">General enquiry</option>
                      <option value="business">Business enquiry</option>
                      <option value="support">Existing project / support</option>
                      <option value="other">Something else</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">Message *</span>
                    <textarea required rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="aa-input" />
                  </label>
                  <button type="submit" disabled={sending} data-testid="contact-submit" className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright disabled:opacity-50 text-white font-display font-bold tracking-widest text-sm px-8 py-3.5 transition-colors">
                    {sending ? "SENDING…" : "SEND MESSAGE"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
